import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Link, ColorTokens } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { get } from 'lodash';
import { ArrowFullRightIcon, ArrowFullRightIconGlyph, TickIconGlyph } from '@shapeable/icons';
import { LinkButton, LinkButtonProps, useEntity, useEntityPathDeriver } from '@shapeable/ui';

const cls = classNames('link-entity-button-swap');

// -------- Types -------->

export type LinkEntityButtonProps = Classable & HasChildren & Omit<LinkButtonProps, 'href'> & { 
  entity: Link;
  showTypeAsSubLabel?: boolean;
  colors?: ColorTokens.TextBackgroundBorder & ColorTokens.Fill;
};

export const LinkEntityButtonDefaultProps: Omit<LinkEntityButtonProps, 'entity'> = {
  showTypeAsSubLabel: true,
};

// -------- Styles -------->

type ContainerProps = {
  _backgroundColor?: string;
};

const CustomButtonStyles = breakpoints({
  base: css`
    max-width: 300px;

    .shp--button {
    
    ${({ _backgroundColor }: ContainerProps) => _backgroundColor && css`
      background: ${theme.COLOR(_backgroundColor)};
      border: none;
    `}
    color: white;
    font-weight: 600;
    padding: 12px 24px;
    transition: all 0.3s ease;
    
    &:hover {
      filter: brightness(1.1);
      transform: translateX(8px);
    }
    
    &:active {
      transform: translateY(0) translateX(8px);
    }
    }
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LinkButton)<ContainerProps>`${CustomButtonStyles}`,
};

export const LinkEntityButton: Shapeable.FC<LinkEntityButtonProps> = (props) => {
  const { className, children, showTypeAsSubLabel, ...rest } = props;
  const entity = useEntity(props.entity);
  
  const getEntityPath = useEntityPathDeriver();

  const { color } = entity;

  const pagePath = entity.page && getEntityPath(entity.page);
  const fileUrl = get(entity, 'file.file.url');
  const url = get(entity, 'url');
  const isDownload = rest.isDownload || !!entity.isDownload || (!pagePath && !!fileUrl);
  const href = pagePath || fileUrl || url;

  const label = children || entity.label || entity.name;
  const type = get(entity, 'type.name');
  const subLabel = showTypeAsSubLabel && type;

  return (
   <My.Container 
     href={href} 
     _backgroundColor={color?.darkValue || color?.value || 'primary'}
     {...rest} 
     subLabel={subLabel} 
     isDownload={isDownload} 
     icon={ArrowFullRightIconGlyph}
     colors={{ fill: 'light' }}
     className={cls.name(className)}
   >
     {label}
   </My.Container>
  )
};

LinkEntityButton.defaultProps = LinkEntityButtonDefaultProps;
LinkEntityButton.cls = cls;